import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { message, Button } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import "./EditTreeForm.css";
import Logo2 from "../../images/MMRCLogo.svg";
import UploadIcon from "../../images/UploadIcon.svg";
import ResetIcon from "../../images/ResetIcon.svg";
import CompensatoryIcon from "../../images/CompensatoryTrees.svg";
import TransplantIcon from "../../images/TransplantTrees.svg";
import InSituIcon from "../../images/InSituTrees.svg";
import UserInfoSVG from "../../images/UserInfoSVG.svg";
import ArrowSVG from "../../images/ArrowSVG.svg";
import { BaseURL } from "../../Utils/baseURL";
import UserAccount from '../Auth/UserAccount';
import LatLongIcon from "../../images/LatLongIcon.svg";

const EditTreeForm = () => {
  const { tid } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");

  const tree = state?.tree || {};

  const [formData, setFormData] = useState({
    // tree_number: tree.properties?.tree_number || "",
    survey_type: tree.properties?.survey_type || "in_situ",
    location: `${tree.geometry?.coordinates?.[1] || ''},${tree.geometry?.coordinates?.[0] || ''}` || "",
    station: tree.properties?.station || "",
    package: tree.properties?.package || "",
    location_description: tree.properties?.location_description || "",
    tree_type: tree.properties?.tree_type || "",
    girth: tree.properties?.girth || "",
    girth_unit: tree.properties?.girth_unit || "cm",
    height: tree.properties?.height || "",
    height_unit: tree.properties?.height_unit || "ft",
    remark: tree.properties?.remark || "",
    image: null,
    project: tree.properties?.project || "",
    plantation_date: tree.properties?.plantation_date ? dayjs(tree.properties.plantation_date) : null,
  });

  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(
    tree.properties?.tree_images?.length > 0
      ? tree.properties.tree_images[tree.properties.tree_images.length - 1].image
      : tree.properties?.image || null
  );
  const [stations, setStations] = useState([]);
  const [treeNames, setTreeNames] = useState([]);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [user, setUser] = useState({});

  useEffect(() => {
    const fetchStations = async () => {
      try {
        const response = await axios.get(`${BaseURL}/program/stations/`, {
          headers: { Authorization: `token ${token}` },
        });
        setStations(response.data.data || []);
      } catch (error) {
        console.error("Error fetching stations:", error);
      }
    };

    const fetchTreeNames = async () => {
      try {
        const response = await axios.get(`${BaseURL}/survey/trees/list/`, {
          headers: { Authorization: `token ${token}` },
        });
        setTreeNames(response.data.data.map(tree => ({
          id: tree.id,
          name: tree.name,
        })));
      } catch (error) {
        console.error("Error fetching tree names:", error);
      }
    };

    fetchStations();
    fetchTreeNames();
  }, [token]);

  const handleFlyToCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setFormData((prevData) => ({
            ...prevData,
            location: `${latitude.toFixed(6)},${longitude.toFixed(6)}`,
          }));
          message.success("Current location fetched successfully!");
        },
        (error) => {
          message.error("Unable to fetch current location.");
        }
      );
    } else {
      message.error("Geolocation is not supported.");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('token');
    setIsAccountOpen(false);
    navigate('/');
    message.success("Logout successful!");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: [ "station", "package", "project", "tree_type"].includes(name)
        ? parseInt(value, 10) || ""
        : value,
    }));
  };

  const handleTreeSelection = (e) => {
    const selectedTreeId = e.target.value;
    const selectedTree = treeNames.find(tree => tree.id === parseInt(selectedTreeId, 10));
    setFormData((prevData) => ({
      ...prevData,
      tree_type: selectedTree ? selectedTree.id : "",
    }));
  };

  const handleImageChange = (e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      setImageFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleClearAll = () => {
    setFormData({
      // tree_number: "",
      survey_type: "in_situ",
      location: "",
      station: "",
      package: "",
      location_description: "",
      tree_type: "",
      girth: "",
      girth_unit: "cm",
      height: "",
      height_unit: "ft",
      remark: "",
      image: null,
      project: "",
      plantation_date: null,
    });
    setImageFile(null);
    setPreviewImage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!formData.tree_type || !formData.station || !formData.project) {
      message.error("Please fill all required fields (Tree Type, Station, Project).");
      return;
    }
  
    // Parse location string into latitude and longitude
    const [latitude, longitude] = formData.location.split(',').map(coord => parseFloat(coord.trim()));
    if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
      message.error("Please enter a valid location in the format 'latitude,longitude'");
      return;
    }
  
    const data = new FormData();
    data.append('survey_type', formData.survey_type.toLowerCase());
    data.append('location', JSON.stringify({
      latitude: latitude,
      longitude: longitude,
    }));
    data.append('station', parseInt(formData.station));
    data.append('package', formData.package ? parseInt(formData.package) : "");
    data.append('location_description', formData.location_description || "");
    data.append('tree_type', parseInt(formData.tree_type));
    data.append('girth', formData.girth ? parseFloat(formData.girth) : "");
    data.append('girth_unit', formData.girth_unit || "");
    data.append('height', formData.height ? parseFloat(formData.height) : "");
    data.append('height_unit', formData.height_unit || "");
    data.append('remark', formData.remark || "");
    data.append('project', parseInt(formData.project));
    if (formData.plantation_date) {
      data.append('plantation_date', dayjs(formData.plantation_date).format("YYYY-MM-DD"));
    }
  
    if (imageFile) {
      
      data.append('image', imageFile);
      data.append('remove_old_image', true); // Ensure old images are removed
    } else if (previewImage) {
     
    }

    // If previewImage exists but no new imageFile, don't append 'image' to leave it unchanged
  
    // Log FormData contents for debugging
    for (let [key, value] of data.entries()) {
      
    }
  
    try {
      const response = await axios.patch(
        `${BaseURL}/survey/tree-surveys/${tid}/update/`,
        data,
        {
          headers: {
            Authorization: `token ${token}`,
            "Content-Type": "multipart/form-data",
            
          },
        }
      );
  
      if (response.status === 200) {
        message.success("Tree data updated successfully!");
        const updatedData = response.data.data.properties;
        // Use the latest image from tree_images
        const latestImage = updatedData.tree_images.length > 0
          ? updatedData.tree_images[updatedData.tree_images.length - 1].image
          : updatedData.image;
        setPreviewImage(latestImage); // Update preview with the latest image
        navigate('/', { state: { updatedTree: response.data.data } });
      }
    } catch (error) {
      console.error("Error details:", error.response?.data || error.message);
      message.error(`Failed to update: ${error.response?.data?.message || error.message}`);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="tree-form">
      <div className="form-header">
        <div className="left-header">
        <div className="logo1-area"  >
             <img src={Logo2} alt="Logo" className="logo1" />
        </div>
          <div className="header-text">
            <h2>Mumbai Metro Line 3</h2>
            <h3>Tree Geotagging - Edit</h3>
          </div>
        </div>
        
        <div className="right-header">
          <div className="header-links">
            <a href="/" className="back-home">
              <img src={ArrowSVG} alt="Arrow" className="arrow-svg" />
              Back to Home
            </a>
            <div className="user-info">
              <Button onClick={() => setIsAccountOpen(!isAccountOpen)} className="user-info-btn">
                <img src={UserInfoSVG} alt="User Info" className="user-info-svg" />
              </Button>
              {isAccountOpen && (
                <UserAccount
                  user={user}
                  onLogout={handleLogout}
                  onClose={() => setIsAccountOpen(false)}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="form-layout">
        <div className="form-left">
          <p className="section-title-left">Edit Tree Details:</p>
          <label className="upload-box">
            <input type="file" className="upload-input" onChange={handleImageChange} />
            <div className="upload-text">
              <img src={UploadIcon} alt="Upload icon" />
              <span>
                Drop your Image here, or <strong>browse</strong>
              </span>
              <p>Supports: JPG and PNG</p>
            </div>
          </label>
          {previewImage && (
            <img src={previewImage} alt="Tree Preview" className="preview-img"  />
          )}
        </div>

        <div className="form-right">
          <div className="form-right-cont">
            <div className="survey-type-container">
              <h4 className="section-title">Survey Type *:</h4>
              <button
                type="button"
                className="clear-all"
                onClick={handleClearAll}
                title="Clear All"
              >
                Clear All<img src={ResetIcon} alt="Reset Icon" />
              </button>
            </div>

            <div className="survey-type">
              {["in_situ", "transplant", "compensatory"].map((type) => (
                <div key={type} className="survey-chip-container">
                  <input
                    type="radio"
                    id={`radio-${type}`}
                    name="survey_type"
                    value={type}
                    checked={formData.survey_type === type}
                    onChange={handleInputChange}
                    className="radio-input"
                  />
                  <label
                    htmlFor={`radio-${type}`}
                    className={`survey-chip ${
                      formData.survey_type === type ? "active" : ""
                    } ${type.toLowerCase()}`}
                  >
                    <span>
                      {type === "in_situ" && <img src={InSituIcon} alt="Insitu Icon" />}
                      {type === "transplant" && <img src={TransplantIcon} alt="Transplant Icon" />}
                      {type === "compensatory" && <img src={CompensatoryIcon} alt="Compensatory Icon" />}
                      {type}
                    </span>
                  </label>
                </div>
              ))}
            </div>

            <div className="tree-details-container">
              <h4 className="section-title">Tree Details*:</h4>
              <div className="tree-details">
                {/* <div className="form-group">
                  <label htmlFor="tree_number">Tree Number</label>
                  <input
                    id="tree_number"
                    type="number"
                    name="tree_number"
                    placeholder="Tree Number"
                    value={formData.tree_number}
                    onChange={handleInputChange}
                  />
                </div> */}

                <div className="form-group">
                  <label htmlFor="project">Project</label>
                  <input
                    id="project"
                    type="number"
                    name="project"
                    placeholder="Project"
                    value={formData.project}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="tree_type">Tree Type</label>
                  <select id="tree_type" name="tree_type" value={formData.tree_type} onChange={handleTreeSelection}>
                    <option value="">Select Tree Type</option>
                    {treeNames.map((tree) => (
                      <option key={tree.id} value={tree.id}>{tree.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="station">Station</label>
                  <select
                    id="station"
                    name="station"
                    value={formData.station || ""}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Station</option>
                    {stations.map((station) => (
                      <option key={station.id} value={station.id}>
                        {station.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="location">Location (lat,lon)</label>
                  <input
                    id="location"
                    type="text"
                    name="location"
                    placeholder="Latitude,Longitude"
                    value={formData.location}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="location">Get Current Location</label>
                  <button
                    type="button"
                    onClick={handleFlyToCurrentLocation}
                    className="lat-long-btn"
                  >
                    <img src={LatLongIcon} alt="Lat Long Icon" />
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="location_description">Location Description</label>
                <textarea
                  id="location_description"
                  name="location_description"
                  placeholder="Location Description"
                  value={formData.location_description}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="additional-details">
              <div className="field-group">
                <label htmlFor="girth">Add Girth</label>
                <div className="input-box">
                  <input
                    id="girth"
                    type="number"
                    name="girth"
                    placeholder="Enter Girth"
                    value={formData.girth}
                    onChange={handleInputChange}
                  />
                  <select
                    name="girth_unit"
                    value={formData.girth_unit}
                    onChange={handleInputChange}
                  >
                    <option value="cm">CM</option>
                    <option value="ft">FT</option>
                    <option value="m">M</option>
                  </select>
                </div>
              </div>

              <div className="field-group">
                <label htmlFor="height">Add Height</label>
                <div className="input-box">
                  <input
                    id="height"
                    type="number"
                    name="height"
                    placeholder="Enter Height"
                    value={formData.height}
                    onChange={handleInputChange}
                  />
                  <select
                    name="height_unit"
                    value={formData.height_unit}
                    onChange={handleInputChange}
                  >
                    <option value="cm">CM</option>
                    <option value="ft">FT</option>
                    <option value="m">M</option>
                  </select>
                </div>
              </div>

              <div className="field-group">
                <label htmlFor="package">Package</label>
                <div className="input-box">
                  <input
                    id="package"
                    type="number"
                    name="package"
                    placeholder="Add Package"
                    value={formData.package}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="remark">Remarks</label>
              <textarea
                id="remark"
                name="remark"
                placeholder="Enter Remarks Here..."
                value={formData.remark}
                onChange={handleInputChange}
              />
            </div>

            <button type="submit" className="submit-button">
              UPDATE
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default EditTreeForm;


